// src/App.tsx - UPDATED March 15, 2026
// Multi-tenant: shows TenantPicker after login if user has multiple tenants.
// V2 routes use DashboardV2/AnalyticsPageV2 (Netlify Functions path).
// V1 routes unchanged.

import { useEffect, useState } from 'react';
import { useAuthContext } from './hooks/useAuth';
import { applyScale, getStoredScale } from './lib/displayPrefs';
import { initTopScrollbars } from './lib/topScrollbars';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import AnalyticsPage from './pages/AnalyticsPage';
// DashboardV2 kept in repo for reference; no longer routed since PR #49.
import AnalyticsPageV2 from './pages/AnalyticsPageV2';
import AnalyticsHubPage from './pages/AnalyticsHubPage';
import LoginPage from './pages/LoginPage';
import LoadingScreen from './components/LoadingScreen';
import ReportScheduleManager from './components/ReportScheduleManager';
import PowerBIReportViewer from './components/PowerBIReportViewer';
import TenantPicker from './components/TenantPicker';
import CacheFreshnessBanner from './components/CacheFreshnessBanner';
import AppShell from './components/AppShell';
import CustomReportsPage from './pages/CustomReportsPage';
import SystemStatusPage from './pages/SystemStatusPage';
import SystemAdminPage from './pages/SystemAdminPage';
import QBODashboardPage from './pages/QBODashboardPage';
import RouteReportsPage from './pages/RouteReportsPage';
import UserControlPage from './pages/UserControlPage';
import SupportPage from './pages/SupportPage';
import BillingPage from './pages/BillingPage';
import ProductionPage from './pages/production/ProductionPage';
import FinancialsPage from './pages/FinancialsPage';

export type Page = 'landing' | 'analytics' | 'detailAnalytics' | 'analyticsV2' | 'detailAnalyticsV2' | 'powerbi' | 'routeReports' | 'schedules' | 'customReports' | 'systemStatus' | 'systemAdmin' | 'quickbooks' | 'userControl' | 'support' | 'billing' | 'production' | 'financials';

// The agent's QBO OAuth callback redirects the browser to
// `${Qbo:FrontendUrl}/oauth-success` or `/oauth-error?message=...`. This SPA
// has no router, so translate that path into "open the QuickBooks page" and
// leave the outcome in sessionStorage for QBODashboardPage to display.
// Used as the initial-state function for `currentPage`, so it runs once.
function consumeOAuthLanding(): Page {
  const path = window.location.pathname;
  if (path !== '/oauth-success' && path !== '/oauth-error') return 'landing';
  const message = new URLSearchParams(window.location.search).get('message') || '';
  try {
    sessionStorage.setItem('qboOAuthResult', JSON.stringify({ ok: path === '/oauth-success', message }));
  } catch { /* storage unavailable — page still opens */ }
  window.history.replaceState(null, '', '/');
  return 'quickbooks';
}

function App() {
  const {
    isAuthenticated, isLoading, login, branding, user,
    needsTenantSelection, tenants, setActiveTenant,
  } = useAuthContext();
  const [currentPage, setCurrentPage] = useState<Page>(consumeOAuthLanding);

  // Apply the saved text/UI scale app-wide on load (see lib/displayPrefs).
  // Default 'normal' (100%) is a no-op, so non-admin users are unaffected.
  useEffect(() => {
    applyScale(getStoredScale());
  }, []);

  // Add a synced top scrollbar above every wide data table (see topScrollbars).
  useEffect(() => initTopScrollbars(), []);

  if (branding?.primary_color) {
    document.documentElement.style.setProperty('--color-primary', branding.primary_color);
  }

  if (isLoading) {
    return <LoadingScreen companyName={branding?.company_name} />;
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={login} branding={branding} />;
  }

  // Multi-tenant: ask which company before showing anything
  if (needsTenantSelection) {
    return (
      <TenantPicker
        tenants={tenants}
        userName={user?.firstName}
        onSelect={(tenant) => {
          setActiveTenant(tenant);
        }}
      />
    );
  }

  // Compute the page content first, then wrap it with the freshness banner.
  // The banner overlays every authenticated view via a fixed-position pill
  // in the top-right; per-page layouts are untouched.
  const pageContent = (() => {
    // -- V1 Routes (existing, UNCHANGED) --
    if (currentPage === 'analytics') {
      return <Dashboard onBack={() => setCurrentPage('landing')} onNavigate={setCurrentPage} />;
    }
    if (currentPage === 'detailAnalytics') {
      return <AnalyticsPage onBack={() => setCurrentPage('analytics')} />;
    }

    // -- V2 Routes (Netlify Functions, multi-tenant) --
    // PR3: 'analyticsV2' now opens the hub. The legacy DashboardV2 and the
    // direct AnalyticsPageV2 routes are kept in case anything links to them
    // (audit logs, bookmarks); the landing tile only points at the hub.
    if (currentPage === 'analyticsV2') {
      return <AnalyticsHubPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'detailAnalyticsV2') {
      return <AnalyticsPageV2 onBack={() => setCurrentPage('analyticsV2')} />;
    }

    // -- Other Routes (UNCHANGED) --
    if (currentPage === 'schedules') {
      return <ReportScheduleManager onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'powerbi') {
      return <PowerBIReportViewer onBack={() => setCurrentPage('landing')} onSettings={() => setCurrentPage('schedules')} />;
    }
    if (currentPage === 'routeReports') {
      return <RouteReportsPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'production') {
      return <ProductionPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'financials') {
      return <FinancialsPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'customReports') {
      return <CustomReportsPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'systemStatus') {
      return <SystemStatusPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'systemAdmin') {
      return <SystemAdminPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'quickbooks') {
      return <QBODashboardPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'userControl') {
      return <UserControlPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'support') {
      return <SupportPage onBack={() => setCurrentPage('landing')} />;
    }
    if (currentPage === 'billing') {
      return <BillingPage onBack={() => setCurrentPage('landing')} />;
    }

    return <LandingPage onNavigate={setCurrentPage} />;
  })();

  return (
    <AppShell currentPage={currentPage} onNavigate={setCurrentPage}>
      <CacheFreshnessBanner />
      {pageContent}
    </AppShell>
  );
}

export default App;